import * as React from 'react';
import { Text, TYPOGRAPHY } from 'wix-ui-tpa/Text';
import { ApiTypes } from '@wix/social-groups-api/dist/src/types';
import { TEXT_BUTTON_PRIORITY, TextButton } from 'wix-ui-tpa/TextButton';
import {
  Block,
  BlockAlign,
  SettingsInput,
  SwitchBlock,
} from '@wix/social-groups-common';
import { InjectedTranslateProps, translate } from 'react-i18next';
import { ClickOutside } from 'wix-ui-core/click-outside';

import styles from './GroupSettings.st.css';

interface TabsSettingsProps {
  tabSettings: ApiTypes.v1.GroupApp[];
  updateTab(tabSettings: ApiTypes.v1.GroupApp);
}

export const GroupTab: { [key in ApiTypes.v1.GroupAppKey]: string } = {
  [ApiTypes.v1.GroupAppKey.FEED_APP]: 'Discussion',
  [ApiTypes.v1.GroupAppKey.GALLERY_APP]: 'Media',
  [ApiTypes.v1.GroupAppKey.MEMBERS_APP]: 'Members',
  [ApiTypes.v1.GroupAppKey.ABOUT_APP]: 'About',
};

const isAlwaysVisible = (key: ApiTypes.v1.GroupAppKey) =>
  key === ApiTypes.v1.GroupAppKey.FEED_APP ||
  key === ApiTypes.v1.GroupAppKey.ABOUT_APP;

const MAX_NAME_LENGTH = 20;

interface TabsSettingsState {
  editApp: ApiTypes.v1.GroupAppKey;
  appName: string;
}

interface TabsSettingsPropsComponent
  extends TabsSettingsProps,
    InjectedTranslateProps {}

export class TabsSettingsComponent extends React.Component<
  TabsSettingsPropsComponent
> {
  state: TabsSettingsState = { appName: '', editApp: null };

  private readonly startEdit = (
    key: ApiTypes.v1.GroupAppKey,
    appName: string,
  ) => {
    this.setState({ editApp: key, appName });
  };
  private readonly stopEdit = e => {
    this.setState({ editApp: null, appName: '' });
  };
  private readonly switchTab = (groupApp: ApiTypes.v1.GroupApp) => {
    return this.props.updateTab({
      ...groupApp,
      installed: !groupApp.installed,
    });
  };

  private readonly renderTabName = (groupApp: ApiTypes.v1.GroupApp) => {
    const { key, customName } = groupApp;
    const tabName = customName || GroupTab[key];
    const rename = this.props.t('groups-web.settings.rename');
    const onEdit = () => this.startEdit(key, tabName);
    return (
      <Block>
        <Text>{tabName}</Text>{' '}
        <TextButton onClick={onEdit} priority={TEXT_BUTTON_PRIORITY.primary}>
          {rename}
        </TextButton>
      </Block>
    );
  };

  private readonly changeName = (groupApp: ApiTypes.v1.GroupApp) => {
    const updatedGroupApp = { ...groupApp };
    const { appName } = this.state;
    updatedGroupApp.customName = appName;
    this.setState({ appName: '', editApp: null });
    return this.props.updateTab(updatedGroupApp);
  };

  private readonly renderTabEdit = (groupApp: ApiTypes.v1.GroupApp) => {
    const { t } = this.props;

    const { appName } = this.state;
    return (
      <ClickOutside
        onClickOutside={this.stopEdit}
        rootRef={{ current: this[this.getRefNameFor(groupApp.key)] }}
      >
        <Block align={BlockAlign.center} {...styles(styles.editBlock)}>
          <SettingsInput
            defaultValue={appName}
            maxLength={MAX_NAME_LENGTH}
            onChange={this.onNameChange}
            autoFocus
          />{' '}
          <Text
            typography={TYPOGRAPHY.runningText}
            className={styles.symbolsCount}
          >
            {appName.length}/{MAX_NAME_LENGTH}
          </Text>
          <TextButton
            priority={TEXT_BUTTON_PRIORITY.primary}
            onClick={() => this.changeName(groupApp)}
          >
            {t('groups-web.settings.tabs.rename.save')}
          </TextButton>
        </Block>
      </ClickOutside>
    );
  };
  private readonly renderSwitchBlock = (groupApp: ApiTypes.v1.GroupApp) => {
    const { key, installed } = groupApp;
    return (
      <SwitchBlock
        onChange={() => this.switchTab(groupApp)}
        checked={installed}
        disabled={isAlwaysVisible(key)}
        key={key}
        className={styles.switchBlock}
      >
        <div ref={el => this.setTabRef(groupApp.key, el)}>
          {this.state.editApp === key
            ? this.renderTabEdit(groupApp)
            : this.renderTabName(groupApp)}
        </div>
      </SwitchBlock>
    );
  };
  render() {
    const { tabSettings } = this.props;
    const caption = 'You can customize tab names & visibility in this group';
    return (
      <>
        <Text
          typography={TYPOGRAPHY.runningText}
          tagName={'span'}
          {...styles(styles.smallCaption)}
        >
          {caption}
        </Text>
        {tabSettings.map(tab => this.renderSwitchBlock(tab))}
      </>
    );
  }

  private readonly onNameChange = e => {
    const appName = e.target.value;
    this.setState({ appName });
  };

  private readonly setTabRef = (
    appKey: ApiTypes.v1.GroupAppKey,
    el: HTMLElement,
  ) => {
    this[this.getRefNameFor(appKey)] = el;
  };

  private readonly getRefNameFor = (appKey: ApiTypes.v1.GroupAppKey) =>
    `${appKey}-ref`;
}

export const TabsSettings = translate()(
  TabsSettingsComponent,
) as React.ComponentType<TabsSettingsProps>;
TabsSettings.displayName = 'TabsSettings';
